/**
 * Password policy for the "Set new password" flow (DEV-3110).
 *
 * Single source of truth shared by the live checklist UI (PasswordChecklist)
 * and the zod schema in routes.tsx. Each rule maps 1:1 to a checklist row and
 * an i18n key under `auth.newPassword.policy.*`.
 */

/**
 * Common special characters shown as a hint in the checklist label. The actual
 * policy (matching backend DEV-3116) accepts ANY non-alphanumeric character —
 * the ticket's `!@#$%` were widened server-side because rejecting common
 * specials like `&*-_=` was poor UX. These are just the examples we surface.
 */
export const PASSWORD_SPECIAL_CHARS = '!@#$%';

/** Any non-alphanumeric character — mirrors backend `HAS_SPECIAL = /[^A-Za-z0-9]/`. */
const SPECIAL_CHAR_RE = /[^A-Za-z0-9]/;
const UPPER_RE = /[A-Z]/;
const LOWER_RE = /[a-z]/;
const NUMBER_RE = /[0-9]/;
/**
 * Printable ASCII, NO space — mirrors backend `ALLOWED_CHARSET = /^[\x21-\x7E]+$/`.
 * Rejects spaces, control chars, and non-Latin scripts (Cyrillic, CJK, etc.).
 */
const LATIN_ONLY_RE = /^[\x21-\x7E]*$/;

export interface PasswordPolicyResult {
  minLength: boolean;
  mixedCase: boolean;
  number: boolean;
  special: boolean;
  /** True when the password does not contain the user's email (or no email is known). */
  notEmail: boolean;
  latinOnly: boolean;
}

/** Ordered list of rule keys — drives checklist row order and i18n lookups. */
export const PASSWORD_RULE_KEYS = [
  'minLength',
  'mixedCase',
  'number',
  'special',
  'notEmail',
  'latinOnly',
] as const satisfies readonly (keyof PasswordPolicyResult)[];

export type PasswordRuleKey = (typeof PASSWORD_RULE_KEYS)[number];

/**
 * Evaluate every policy rule against a candidate password.
 *
 * @param password Candidate password.
 * @param email    The user's email, when known. The `notEmail` rule passes
 *                 automatically when the email is absent (we cannot enforce
 *                 what we don't have — see DEV-3110 reset contract).
 */
export function checkPasswordPolicy(password: string, email?: string | null): PasswordPolicyResult {
  const normalizedEmail = email?.trim().toLowerCase() ?? '';
  const localPart = normalizedEmail.split('@')[0] ?? '';

  const containsEmail =
    normalizedEmail.length > 0 &&
    (password.toLowerCase().includes(normalizedEmail) ||
      (localPart.length > 0 && password.toLowerCase().includes(localPart)));

  return {
    minLength: password.length >= 8,
    mixedCase: UPPER_RE.test(password) && LOWER_RE.test(password),
    number: NUMBER_RE.test(password),
    special: SPECIAL_CHAR_RE.test(password),
    notEmail: !containsEmail,
    latinOnly: LATIN_ONLY_RE.test(password),
  };
}

/** True when the password satisfies every policy rule. */
export function isPasswordValid(password: string, email?: string | null): boolean {
  const result = checkPasswordPolicy(password, email);
  return PASSWORD_RULE_KEYS.every((key) => result[key]);
}

/* ── Generator ─────────────────────────────────────────────────────────────── */

/**
 * Character pools for the generator. Ambiguous glyphs (`O`/`0`, `l`/`1`/`I`)
 * are excluded — a generated password is usually read off a screen and typed
 * or dictated somewhere else, so confusable characters cost more than the
 * handful of bits of entropy they add.
 */
const GEN_UPPER = 'ABCDEFGHJKLMNPQRSTUVWXYZ';
const GEN_LOWER = 'abcdefghijkmnopqrstuvwxyz';
const GEN_NUMBER = '23456789';
/** Kept to the specials the policy hint actually advertises. */
const GEN_SPECIAL = '!@#$%';
const GEN_ALL = GEN_UPPER + GEN_LOWER + GEN_NUMBER + GEN_SPECIAL;

export const GENERATED_PASSWORD_LENGTH = 16;

/** Uniform random index in `[0, max)` — rejection-sampled to avoid modulo bias. */
function randomIndex(max: number): number {
  const limit = Math.floor(0xffffffff / max) * max;
  const buf = new Uint32Array(1);
  let value = 0;
  do {
    crypto.getRandomValues(buf);
    value = buf[0] ?? 0;
  } while (value >= limit);
  return value % max;
}

function pick(pool: string): string {
  return pool.charAt(randomIndex(pool.length));
}

/**
 * Generate a password that satisfies every policy rule.
 *
 * Seeds one character from each required pool so the result can't fail a rule,
 * fills the rest from the combined pool, then Fisher–Yates shuffles so the
 * seeded characters aren't always in the same positions. Uses
 * `crypto.getRandomValues` rather than `Math.random` — this produces a real
 * credential, and `Math.random` is not a CSPRNG.
 */
export function generatePassword(length: number = GENERATED_PASSWORD_LENGTH): string {
  const size = Math.max(length, 8);
  const chars = [pick(GEN_UPPER), pick(GEN_LOWER), pick(GEN_NUMBER), pick(GEN_SPECIAL)];
  while (chars.length < size) chars.push(pick(GEN_ALL));

  for (let i = chars.length - 1; i > 0; i--) {
    const j = randomIndex(i + 1);
    const a = chars[i] as string;
    chars[i] = chars[j] as string;
    chars[j] = a;
  }

  return chars.join('');
}
